import { compose, withStateHandlers, mapProps, lifecycle } from 'recompose'
import { keys, reduce, upperFirst, map } from 'lodash'
import withStateChanged from 'hocs/bases/withStateChanged'
import withLoading from 'hocs/bases/withLoading'
import withSnackBar from 'store/snackbar/withSnackBar'

const status = {
  INITIAL: 'initial',
  REQUEST: 'request',
  SUCCESS: 'success',
  FAILURE: 'failure',
}

const setStatus = (key, status, getMessage) => (
  (_, props) => (
    (error = null) => ({
      [key]: {
        status,
        message: getMessage(props),
        error,
      }
    })
  )
)

const createTempResourceState = (resourceByKeys, data) => {
  return reduce(resourceByKeys, (result, value, key) => {
    const resourceKeys = keys(resourceByKeys)
    const index = resourceKeys.indexOf(key)
    return {
      ...result,
      [key]: data[index]
    }
  }, {})
}

const withDynamicEditPage = ({
  resourceByKeys = {},
  fetchAllResourcesStatusName = 'fetchAllResourcesStatus',
  updateAllResourcesStatusName = 'updateAllResourcesStatus',
  getFetchInitialMessage = () => '',
  getFetchRequestMessage = () => '',
  getFetchSuccessMessage = () => '',
  getFetchFailureMessage = () => '',
  getUpdateInitialMessage = () => '',
  getUpdateRequestMessage = () => '',
  getUpdateSuccessMessage = () => '',
  getUpdateFailureMessage = () => '',
}) => {
  return compose(
    withSnackBar,
    withStateChanged(),
    withStateHandlers(
      {
        ...reduce(resourceByKeys, (result, value, key) => ({
          ...result,
          [key]: value.initialState,
        }), {}),
        fetchStatus: {
          status: status.INITIAL,
          error: null,
          message: null,
        },
        updateStatus: {
          status: status.INITIAL,
          error: null,
          message: null,
        }
      },
      {
        ...reduce(resourceByKeys, (result, value, key) => {
          const ResourceName = upperFirst(key)
          return {
            ...result,
            [`set${ResourceName}`]: () => (newData) => ({
              [key]: newData,
            })
          }
        }, {}),
        resetState: (_, { tempState }) => () => (reduce(resourceByKeys, (result, value, key) => ({
          ...result,
          [key]: tempState[key],
        }), {})),
        setFetchStatusInitial: setStatus('fetchStatus', status.INITIAL, getFetchInitialMessage),
        setFetchStatusRequest: setStatus('fetchStatus', status.REQUEST, getFetchRequestMessage),
        setFetchStatusSuccess: setStatus('fetchStatus', status.SUCCESS, getFetchSuccessMessage),
        setFetchStatusFailure: setStatus('fetchStatus', status.FAILURE, getFetchFailureMessage),
        setUpdateStatusInitial: setStatus('updateStatus', status.INITIAL, getUpdateInitialMessage),
        setUpdateStatusRequest: setStatus('updateStatus', status.REQUEST, getUpdateRequestMessage),
        setUpdateStatusSuccess: setStatus('updateStatus', status.SUCCESS, getUpdateSuccessMessage),
        setUpdateStatusFailure: setStatus('updateStatus', status.FAILURE, getUpdateFailureMessage),
      }
    ),
    mapProps(({
      setFetchStatusRequest,
      setFetchStatusSuccess,
      setFetchStatusFailure,
      setUpdateStatusRequest,
      setUpdateStatusSuccess,
      setUpdateStatusFailure,
      fetchStatus,
      updateStatus,
      showMessage,
      ...props,
    }) => {
      return {
        onFetchAllResources: () => {
          setFetchStatusRequest()
          return Promise.all(
            map(
              resourceByKeys,
              ({ fetchResource, getSuccessData = ({ data }) => data }, key) => fetchResource(props)
                .then((data) => {
                  const ResourceName = upperFirst(key)
                  const resolvedData = getSuccessData(data)
                  props[`set${ResourceName}`](resolvedData)
                  return resolvedData
                })
            )
          ).then((data) => {
            setFetchStatusSuccess()
            props.setTempState(createTempResourceState(resourceByKeys, data))
          })
            .catch((error) => {
              console.log('error', error)
              setFetchStatusFailure(error)
            })
        },
        onUpdateAllResources: (onUpdateSuccess) => {
          setUpdateStatusRequest()
          showMessage({ message: null })
          return Promise.all(
            map(
              resourceByKeys,
              ({ updateResource, parseData = (data) => data }, key) => updateResource(parseData(props[key]))
            )
          ).then((data) => {
            showMessage({ action: 'update', message: 'Page Locations' })
            setUpdateStatusSuccess()
            props.setTempState(
              createTempResourceState(
                resourceByKeys,
                Object.keys(resourceByKeys).map((key) => props[key])
              )
            )
            return onUpdateSuccess(data)
          })
            .catch((error) => {
              console.log('error', error)
              showMessage({ message: 'Unable to update! Please contact Muze support.' })
              setUpdateStatusFailure(error)
            })
        },
        [fetchAllResourcesStatusName]: {
          isInitial: fetchStatus.status === status.INITIAL,
          isRequest: fetchStatus.status === status.REQUEST,
          isSuccess: fetchStatus.status === status.SUCCESS,
          isFailure: fetchStatus.status === status.FAILURE,
          errorMessage: fetchStatus.error ? fetchStatus.error.message : '',
          message: fetchStatus.message || '',
        },
        [updateAllResourcesStatusName]: {
          isInitial: updateStatus.status === status.INITIAL,
          isRequest: updateStatus.status === status.REQUEST,
          isSuccess: updateStatus.status === status.SUCCESS,
          isFailure: updateStatus.status === status.FAILURE,
          errorMessage: updateStatus.error ? updateStatus.error.message : '',
          message: updateStatus.message || '',
        },
        ...props
      }
    }),
    lifecycle({
      componentDidMount() {
        this.props.onFetchAllResources()
      },
    }),
    withLoading({
      getIsLoading: props => props[fetchAllResourcesStatusName].isRequest,
      getMessage: props => props[fetchAllResourcesStatusName].message,
    }),
  )
}

export default withDynamicEditPage



// WEBPACK FOOTER //
// ./src/hocs/extends/withDynamicEditPage.js